import { LIVELY_TALK_DRIVER_URL, NATURAL_TALK_DRIVER_URL } from '@/constants';
import { useRemoveWatermarkAccess } from '@/hooks/useRemoveWatermarkAccess';
import { useTiming } from '@/hooks/useTiming';
import { getPageProperty, PolotnoStoreActivePageKeys } from '@/pages/AdvancedEditor/hooks/polotno-store';
import { getAvatarElementFromPage } from '@/pages/AdvancedEditor/utils';
import { generateProject } from '@/services/api';
import { GET_PROJECTS_KEY } from '@/state/video-library/videoLibrary';
import { Emotion } from '@/utils/types';
import { Presenter, ScenePresenter, VideoType, VoiceAccess } from '@d-id/types';
import AmazonS3URI from 'amazon-s3-uri';
import { AxiosHeaders } from 'axios';
import { useQueryClient } from 'react-query';
import { Page } from '../AdvancedEditor';
import { useAdvancedEditorContext } from '../context/useAdvancedEditorContext';
import { useProjectRenderer } from './useProjectRenderer';
import { useUpdateDuration } from './useUpdateDuration';

export type ProjectPagesImages = Record<string, { url: string; overlayUrl?: string }>;

export function getValidUrl(url?: string) {
    try {
        const { bucket, key } = AmazonS3URI(url as string);
        return `s3://${bucket}/${key}`;
    } catch (e) {}

    return url;
}

export function getPresenter(page: Page, fallbackDriver?: string): ScenePresenter | undefined {
    const avatar = getAvatarElementFromPage(page);
    const emotion = getPageProperty(page, PolotnoStoreActivePageKeys.selectedEmotion);
    const selectedDriver = getPageProperty(page, PolotnoStoreActivePageKeys.selectedDriver) || fallbackDriver;

    if (!avatar) {
        return;
    }

    const presenter = avatar.custom.presenter as Presenter;

    const position = {
        top: avatar.y.toString(),
        left: avatar.x.toString(),
        height: avatar.height.toString(),
    };
    const { type } = presenter;

    switch (type) {
        case VideoType.TALK:
            return {
                type,
                source_url: presenter.url,
                position,
                config: {
                    ...(emotion &&
                        emotion !== Emotion.Natural && {
                            driver_expressions: {
                                expressions: [
                                    {
                                        expression: emotion,
                                        start_frame: 0,
                                        intensity: emotion === Emotion.Serious ? 0.8 : 0.6,
                                    },
                                ],
                            },
                        }),
                },
                driver_url:
                    selectedDriver && [NATURAL_TALK_DRIVER_URL, LIVELY_TALK_DRIVER_URL].includes(selectedDriver)
                        ? selectedDriver
                        : undefined,
            };
        case VideoType.Scene:
            return {
                type,
                avatar_id: presenter.id,
                position,
            };
        case VideoType.CLIP:
        default:
            return {
                type: VideoType.CLIP,
                presenter_id: presenter.id,
                driver_id: selectedDriver || presenter.driverId,
                position,
            };
    }
}

export default function (projectId: string) {
    const queryClient = useQueryClient();
    const { removeWatermarkSelected } = useRemoveWatermarkAccess();
    const projectRenderer = useProjectRenderer();
    const { store } = useAdvancedEditorContext();
    const { videoDuration } = useUpdateDuration();
    const { withTiming } = useTiming();

    return async () => {
        if (!store.activePage) {
            return null;
        }

        const isUserClonedVoice = store.pages.some(page => {
            const voice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice);
            return voice?.access === VoiceAccess.ExternalPrivate;
        });

        let headers: AxiosHeaders | undefined;
        if (isUserClonedVoice) {
            headers = new AxiosHeaders();
            headers.set('x-api-key-external', '{"elevenlabs":"****"}');
        }

        await withTiming('generateProject', () =>
            generateProject(projectId, undefined, projectRenderer, removeWatermarkSelected, headers, videoDuration)
        );
        queryClient.invalidateQueries(GET_PROJECTS_KEY).catch(e => console.error(e));
    };
}
